import { useEffect, useRef, useState, type ReactNode } from "react";
import { MoreHorizontal, X } from "lucide-react";

export const cx = (...xs: (string | false | null | undefined)[]) => xs.filter(Boolean).join(" ");

export function PageHeader({ title, sub, actions }: { title: ReactNode; sub?: ReactNode; actions?: ReactNode }) {
  return (
    <div className="flex flex-wrap items-start justify-between gap-3">
      <div className="min-w-0">
        <h1 className="text-[1.45rem] font-semibold tracking-tight text-ink">{title}</h1>
        {sub && <div className="mt-0.5 text-sm text-muted">{sub}</div>}
      </div>
      {actions && <div className="flex flex-wrap items-center gap-2">{actions}</div>}
    </div>
  );
}

export function Card({ title, actions, children, className, pad = true }: { title?: ReactNode; actions?: ReactNode; children: ReactNode; className?: string; pad?: boolean }) {
  return (
    <section className={cx("min-w-0 rounded-xl border border-line bg-surface", className)}>
      {(title || actions) && (
        <header className="flex flex-wrap items-center justify-between gap-2 border-b border-line px-5 py-3">
          <h2 className="text-[0.95rem] font-semibold text-ink">{title}</h2>
          <div className="flex flex-wrap items-center gap-2">{actions}</div>
        </header>
      )}
      <div className={pad ? "p-5" : ""}>{children}</div>
    </section>
  );
}

type Tone = "primary" | "danger" | "ghost" | "success" | "default";
/** One primary (accent) action per area; red only for destructive actions. "success" is kept as an alias of primary. */
export function Button({ tone = "default", className, ...p }: React.ButtonHTMLAttributes<HTMLButtonElement> & { tone?: Tone }) {
  const tones: Record<Tone, string> = {
    primary: "bg-accent text-accent-ink hover:opacity-90 disabled:opacity-40",
    success: "bg-accent text-accent-ink hover:opacity-90 disabled:opacity-40",
    danger: "bg-bad text-white hover:opacity-90 disabled:opacity-40 dark:text-bg",
    ghost: "text-accent-text hover:bg-accent-soft disabled:text-muted disabled:hover:bg-transparent",
    default: "border border-line bg-surface text-ink hover:bg-surface-2 disabled:text-muted",
  };
  return <button {...p} className={cx("inline-flex items-center justify-center gap-1.5 rounded-lg px-3 py-1.5 text-sm font-medium transition-colors disabled:cursor-not-allowed", tones[tone], className)} />;
}

/** Status chips: neutral by default; colour only means something (good / warning / problem / accent). */
const CHIP: Record<string, "good" | "warn" | "bad" | "accent" | "neutral" | "solid"> = {
  Active: "good",
  Completed: "neutral",
  Enrolled: "accent",
  Paused: "neutral",
  Replied: "accent",
  Bounced: "bad",
  "Do Not Contact": "solid",
  ready: "accent",
  approved: "good",
  pending: "neutral",
  queued: "neutral",
  drafting: "neutral",
  rejected: "bad",
  failed: "bad",
  scheduled: "neutral",
  sending: "neutral",
  sent: "good",
  done: "good",
  skipped: "neutral",
  open: "accent",
  paused: "neutral",
  draft: "warn",
  active: "good",
  proposed: "warn",
  accepted: "good",
  held: "warn",
  customized: "accent",
  signal: "neutral",
  live: "good",
  test: "warn",
  "dry-run": "neutral",
};
const CHIP_CLS = {
  good: "bg-good-soft text-good",
  warn: "bg-warn-soft text-warn",
  bad: "bg-bad-soft text-bad",
  accent: "bg-accent-soft text-accent-text",
  neutral: "bg-surface-2 text-ink-2",
  solid: "bg-ink text-bg",
};
export function Badge({ children, tone }: { children: ReactNode; tone?: string }) {
  const k = tone ?? String(children);
  return <span className={cx("inline-flex items-center whitespace-nowrap rounded-full px-2 py-0.5 text-xs font-medium", CHIP_CLS[CHIP[k] ?? (["good", "warn", "bad", "accent", "neutral", "solid"].includes(k) ? (k as keyof typeof CHIP_CLS) : "neutral")])}>{children}</span>;
}

export function LowConfidence() {
  return <Badge tone="warn">Low confidence</Badge>;
}

export function Field({ label, help, error, children }: { label: string; help?: ReactNode; error?: string | null; children: ReactNode }) {
  return (
    <label className="block space-y-1">
      <span className="text-xs font-medium text-ink-2">{label}</span>
      {children}
      {help && !error && <span className="block text-xs text-muted">{help}</span>}
      {error && <span className="block text-xs text-bad">{error}</span>}
    </label>
  );
}
export const inputCls = "w-full rounded-lg border border-line bg-surface px-3 py-1.5 text-sm text-ink placeholder:text-muted focus:border-accent focus:outline-none";

export function Modal({ title, onClose, children, actions, wide }: { title: string; onClose: () => void; children: ReactNode; actions?: ReactNode; wide?: boolean }) {
  useEffect(() => {
    const k = (e: KeyboardEvent) => e.key === "Escape" && onClose();
    window.addEventListener("keydown", k);
    return () => window.removeEventListener("keydown", k);
  }, [onClose]);
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4" onClick={onClose}>
      <div className={cx("w-full rounded-xl border border-line bg-surface shadow-xl", wide ? "max-w-3xl" : "max-w-lg")} onClick={(e) => e.stopPropagation()} role="dialog" aria-modal aria-label={title}>
        <header className="flex items-center justify-between border-b border-line px-5 py-3">
          <span className="text-sm font-semibold">{title}</span>
          <button className="rounded p-1 text-muted hover:bg-surface-2" onClick={onClose} aria-label="Close">
            <X size={16} />
          </button>
        </header>
        <div className="max-h-[70vh] space-y-3 overflow-auto px-5 py-4 text-sm">{children}</div>
        {actions && <footer className="flex justify-end gap-2 border-t border-line px-5 py-3">{actions}</footer>}
      </div>
    </div>
  );
}

/** A small "More" menu for secondary actions (e.g. Remove). */
export function Menu({ items, label = "More" }: { items: { label: string; onClick: () => void; danger?: boolean; disabled?: boolean }[]; label?: string }) {
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);
  useEffect(() => {
    const close = (e: MouseEvent) => ref.current && !ref.current.contains(e.target as Node) && setOpen(false);
    document.addEventListener("mousedown", close);
    return () => document.removeEventListener("mousedown", close);
  }, []);
  return (
    <div className="relative" ref={ref}>
      <Button onClick={() => setOpen(!open)} aria-haspopup="menu" aria-expanded={open}>
        <MoreHorizontal size={15} /> {label}
      </Button>
      {open && (
        <div role="menu" className="absolute right-0 z-30 mt-1 min-w-44 rounded-lg border border-line bg-surface p-1 shadow-lg">
          {items.map((it) => (
            <button key={it.label} role="menuitem" disabled={it.disabled} onClick={() => { setOpen(false); it.onClick(); }} className={cx("block w-full rounded-md px-3 py-1.5 text-left text-sm hover:bg-surface-2 disabled:text-muted", it.danger ? "text-bad" : "text-ink")}>
              {it.label}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

/** Sticky action bar at the bottom of long pages (approve / edit / reject). */
export function StickyBar({ children }: { children: ReactNode }) {
  return <div className="sticky bottom-0 z-20 -mx-4 mt-4 flex flex-wrap items-center justify-end gap-2 border-t border-line bg-surface/95 px-4 py-3 backdrop-blur md:-mx-8 md:px-8">{children}</div>;
}

export function ErrorNote({ error }: { error?: string | null }) {
  if (!error) return null;
  return <div className="rounded-lg border border-bad/30 bg-bad-soft px-3 py-2 text-sm text-bad">{error}</div>;
}
export function Notice({ children, tone = "neutral" }: { children: ReactNode; tone?: "neutral" | "warn" | "accent" }) {
  return <div className={cx("rounded-lg border px-3 py-2 text-sm", tone === "warn" ? "border-warn/30 bg-warn-soft text-warn" : tone === "accent" ? "border-accent/20 bg-accent-soft text-accent-text" : "border-line bg-surface-2 text-ink-2")}>{children}</div>;
}

export function Skeleton({ className }: { className?: string }) {
  return <div className={cx("skeleton h-4", className)} />;
}
export function Loading({ rows = 4 }: { rows?: number }) {
  return (
    <div className="space-y-3 py-4" aria-busy aria-label="Loading">
      <Skeleton className="h-6 w-1/3" />
      {Array.from({ length: rows }).map((_, i) => (
        <Skeleton key={i} className={i % 2 ? "w-5/6" : "w-full"} />
      ))}
    </div>
  );
}
export function Empty({ children, title, action }: { children?: ReactNode; title?: string; action?: ReactNode }) {
  return (
    <div className="flex flex-col items-center gap-2 py-8 text-center">
      {title && <div className="text-sm font-medium text-ink">{title}</div>}
      {children && <div className="max-w-md text-sm text-muted">{children}</div>}
      {action}
    </div>
  );
}

export function Table({ head, children }: { head: ReactNode[]; children: ReactNode }) {
  return (
    <div className="-mx-5 overflow-x-auto">
      <table className="min-w-full text-sm">
        <thead>
          <tr className="border-b border-line text-left text-xs font-medium text-muted">
            {head.map((h, i) => (
              <th key={i} className="whitespace-nowrap px-5 py-2 font-medium">
                {h}
              </th>
            ))}
          </tr>
        </thead>
        <tbody className="divide-y divide-line">{children}</tbody>
      </table>
    </div>
  );
}
export const Td = ({ children, className }: { children?: ReactNode; className?: string }) => <td className={cx("px-5 py-2.5 align-top text-ink-2", className)}>{children}</td>;

/** Runs an async action with a busy flag and an error message. */
export function useAction() {
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const run = async <T,>(fn: () => Promise<T>): Promise<T | undefined> => {
    setBusy(true);
    setError(null);
    try {
      return await fn();
    } catch (e) {
      setError((e as Error).message);
      return undefined;
    } finally {
      setBusy(false);
    }
  };
  return { busy, error, run, setError };
}

export const fmtDate = (iso?: string) => (iso ? new Date(iso).toLocaleString(undefined, { month: "short", day: "numeric", hour: "numeric", minute: "2-digit" }) : "");
export const fmtDay = (d?: string) => (d ? new Date(`${d.slice(0, 10)}T12:00:00`).toLocaleDateString(undefined, { weekday: "short", month: "short", day: "numeric" }) : "");
export const pct = (n: number) => `${Math.round(n * 100)}%`;

export function Stat({ label, value, sub, tone, onClick, active }: { label: string; value: ReactNode; sub?: ReactNode; tone?: "good" | "warn" | "bad"; onClick?: () => void; active?: boolean }) {
  const Tag = onClick ? "button" : "div";
  return (
    <Tag onClick={onClick} className={cx("rounded-xl border bg-surface p-4 text-left", active ? "border-accent" : "border-line", onClick && "hover:border-accent")}>
      <div className="text-xs font-medium text-muted">{label}</div>
      <div className={cx("mt-1 text-2xl font-semibold tabular-nums", tone === "good" && "text-good", tone === "warn" && "text-warn", tone === "bad" && "text-bad")}>{value}</div>
      {sub && <div className="mt-0.5 text-xs text-muted">{sub}</div>}
    </Tag>
  );
}

/** Readable labels for raw field keys from the target list (payrollAgentFit → "Payroll Agent fit"). */
const FIELD_LABELS: Record<string, string> = {
  payrollAgentFit: "Payroll Agent fit",
  payablesMgmtFit: "Payables Management fit",
  timeEntryFit: "Time Entry fit",
  expenseMgmtFit: "Expense Management fit",
  inforHcmPayroll: "Infor HCM Payroll",
  s3Financials: "S3 Financials",
  hrTalent: "HR Talent",
  inforWfm: "Infor WFM",
  inforExpenseMgmt: "Infor Expense Management",
  velocitySuite: "Velocity Suite",
};
export const fieldLabel = (k: string) => FIELD_LABELS[k] ?? k.replace(/([a-z])([A-Z])/g, "$1 $2").replace(/^./, (c) => c.toUpperCase());
